/**
 * Helper functions supporting the "Completion by each assignee" feature.
 *
 * When an action has more than one assignee, the plan owner can choose to track
 * completion separately for each assignee. If enabled, a single action with multiple
 * assignees is split into several single-assignee actions (one per assignee) instead
 * of saving one action shared by all assignees.
 */

export interface SplitCardsResult {
  primaryCard: any;
  extraCards: any[];
}

// The checkbox is only relevant once more than one assignee has been selected.
export const shouldShowCompletionCheckbox = (assignees?: any[]) => (assignees?.length || 0) > 1;

/**
 * Resolves the list of assignee hltIds to use for a submit payload.
 *
 * The "assignees" form field (populated via the assignee Autocomplete) can hold either
 * raw hltId strings or full user objects, depending on whether it was set by user
 * interaction or by the Autocomplete's initial mount effect. This normalizes both shapes
 * and falls back to the card's existing assignee list when the form field is empty
 * (e.g. when a user opens an existing multi-assignee card and doesn't touch the
 * assignee field before saving).
 */
export const extractAssigneeHltIds = (formAssignees: any, fallbackIds?: string[]): string[] => {
  if (Array.isArray(formAssignees) && formAssignees.length) {
    return formAssignees
      .map((assignee: any) => (typeof assignee === "string" ? assignee : assignee?.hltId))
      .filter(Boolean);
  }
  return fallbackIds || [];
};

/**
 * Resolves the final assignee hltId list to submit for a card.
 *
 * Template plans don't render the assignee Autocomplete (see MyPlanActionDetail) and
 * aren't assigned to specific users, so they should never carry an `assignee` in the
 * submit payload.
 */
export const resolveAssignee = (
  isTemplatePlan: boolean,
  assigneeVal: string[] | undefined,
  formAssignees: any,
  activeCardAssignee?: string[]
): string[] | undefined => {
  if (isTemplatePlan) return undefined;
  if (assigneeVal !== undefined) return assigneeVal;
  return extractAssigneeHltIds(formAssignees, activeCardAssignee);
};

/**
 * Splits an assignee hltId list into a primary assignee and the remaining "extra"
 * assignees, if `trackCompletionSeparately` is enabled and there is more than one
 * assignee. Returns `null` when no split is required.
 */
export const splitAssigneeIds = (
  assigneeIds: string[] | undefined,
  trackCompletionSeparately: boolean
): { primaryAssigneeId: string; extraAssigneeIds: string[] } | null => {
  const ids = assigneeIds || [];
  if (!trackCompletionSeparately || ids.length <= 1) return null;

  const [primaryAssigneeId, ...extraAssigneeIds] = ids;
  return { primaryAssigneeId, extraAssigneeIds };
};

/**
 * Splits a card into a primary card (keeps the card's id, if any, and the first assignee)
 * and a list of extra cards (no id, one per remaining assignee) that should be created as
 * new, separate actions.
 *
 * If `trackCompletionSeparately` is false, or there is only a single assignee, the card is
 * returned unchanged with no extra cards.
 */
export const splitCardByAssignee = (
  cardData: any,
  trackCompletionSeparately: boolean,
  isNew?: boolean
): SplitCardsResult => {
  const split = splitAssigneeIds(cardData?.assignee, trackCompletionSeparately);
  if (!split) {
    return { primaryCard: cardData, extraCards: [] };
  }

  const { primaryAssigneeId, extraAssigneeIds } = split;
  const { id, ...cardWithoutId } = cardData;

  const primaryCard = { ...cardData, assignee: [primaryAssigneeId], isIndividualActionRequired: !isNew };
  const extraCards = extraAssigneeIds.map((hltId: string) => ({
    ...cardWithoutId,
    assignee: [hltId],
    isIndividualActionRequired: true
  }));

  return { primaryCard, extraCards };
};
